import type { ChapterDefinition, ChapterId, Season } from "../types";

export const chapters: ChapterDefinition[] = [
  {
    id: "chapter-1",
    number: "壹",
    title: "自然在说什么",
    subtitle: "辨认物候，重建四季次序",
    minutes: 5,
    score: 25,
    paragraphs: ["P01", "P02", "P03"],
    accent: "#d45f58"
  },
  {
    id: "chapter-2",
    number: "贰",
    title: "活的仪器",
    subtitle: "比较生物观察与气象读数",
    minutes: 3,
    score: 15,
    paragraphs: ["P04", "P05"],
    accent: "#2f7562"
  },
  {
    id: "chapter-3",
    number: "叁",
    title: "春天为何迟到",
    subtitle: "改变四个条件，验证物候差异",
    minutes: 8,
    score: 40,
    paragraphs: ["P06", "P07", "P08", "P09", "P10"],
    accent: "#2f6f98"
  },
  {
    id: "chapter-4",
    number: "肆",
    title: "让物候服务生产",
    subtitle: "把观察证据转化为农业决策",
    minutes: 4,
    score: 20,
    paragraphs: ["P11", "P12"],
    accent: "#a36a2b"
  }
];

export const classificationCards = [
  { id: "sprout-card", label: "草木萌发", icon: "芽", assetId: "2B-01", category: "phenology", season: "spring" },
  { id: "peach", label: "桃花盛开", icon: "花", assetId: "2B-02", category: "phenology", season: "spring" },
  { id: "swallow-card", label: "燕子归来", icon: "燕", assetId: "2B-03", category: "phenology", season: "spring" },
  { id: "cuckoo", label: "布谷鸟到来", icon: "鸟", assetId: "2B-04", category: "phenology", season: "spring" },
  { id: "fruit", label: "果实成熟", icon: "果", assetId: "2B-05", category: "phenology", season: "autumn" },
  { id: "yellow-leaf", label: "树叶变黄飘落", icon: "叶", assetId: "2B-06", category: "phenology", season: "autumn" },
  { id: "geese", label: "北雁南飞", icon: "雁", assetId: "2B-07", category: "phenology", season: "autumn" },
  { id: "insects", label: "昆虫销声匿迹", icon: "虫", assetId: "2B-08", category: "phenology", season: "autumn" },
  { id: "melt", label: "冰雪消融", icon: "融", assetId: "2B-09", category: "seasonal", season: "spring" },
  { id: "snow", label: "风雪载途", icon: "雪", assetId: "2B-10", category: "seasonal", season: "winter" },
  { id: "rain", label: "短时暴雨", icon: "雨", assetId: "2B-11", category: "event", season: "summer" },
  { id: "rock", label: "山石滚落", icon: "石", assetId: "2B-12", category: "event", season: "summer" }
] as const;

export const classificationBins = [
  { id: "phenology", label: "物候现象", hint: "生物随时节出现、并会年年重复的变化" },
  { id: "seasonal", label: "季节性自然现象", hint: "会随季节出现，但不是生物发生的变化" },
  { id: "event", label: "偶发自然事件", hint: "偶然发生，不会随时节稳定重复" }
] as const;

export const seasonalSequence = [
  { id: "melt-sprout", label: "冰雪融化，草木萌发", assetId: "2C-01", season: "spring" as Season },
  { id: "swallow", label: "燕子翩然归来", assetId: "2C-02", season: "spring" as Season },
  { id: "cuckoo-fruit", label: "布谷鸟到来，植物孕育果实", assetId: "2C-03", season: "summer" as Season },
  { id: "ripe-leaf", label: "果实成熟，树叶变黄", assetId: "2C-04", season: "autumn" as Season },
  { id: "geese-insects", label: "北雁南飞，昆虫销声匿迹", assetId: "2C-05", season: "autumn" as Season },
  { id: "winter", label: "衰草连天，风雪将至", assetId: "2C-06", season: "winter" as Season }
];

export const livingInstrumentOptions = [
  { id: "peach-tree", label: "桃树", assetId: "3A-03", type: "living", detail: "桃花开放等物候记录可综合反映气候条件" },
  { id: "swallow", label: "燕子", assetId: "3B-01", type: "living", detail: "燕子归来等物候记录可反映时节推移" },
  { id: "thermometer", label: "温度计", type: "device", detail: "测量某一时刻的温度" },
  { id: "hygrometer", label: "湿度计", type: "device", detail: "测量某一时刻的湿度" }
] as const;

export const factorLabs = [
  {
    id: "latitude",
    label: "纬度",
    title: "南北春信",
    compare: "南京桃花比北京早开二十天；到晚春，刺槐只早十天。",
    instruction: "拖入影响因素，再移动南北观察尺。",
    paragraph: "P07",
    sceneAssetId: "4A-01",
    evidenceAssetId: "4E-01",
    low: "南京 · 较早",
    high: "北京 · 较迟"
  },
  {
    id: "longitude",
    label: "经度（海陆差异）",
    title: "温暖的内陆",
    compare: "大连的连翘和榆叶梅比北京迟约一个星期。",
    instruction: "拖入影响因素，再比较沿海与内陆。",
    paragraph: "P08",
    sceneAssetId: "4B-01",
    evidenceAssetId: "4E-02",
    low: "北京",
    high: "大连"
  },
  {
    id: "elevation",
    label: "高下",
    title: "山地花线",
    compare: "春夏越往高处抽青、开花越迟；秋季落叶越早。",
    instruction: "拖入影响因素，再抬升观察位置。",
    paragraph: "P09",
    sceneAssetId: "4C-01",
    evidenceAssetId: "4E-03",
    low: "山脚",
    high: "山顶"
  },
  {
    id: "era",
    label: "古今",
    title: "年轮里的九天",
    compare: "1921—1930年的平均物候比1741—1750年早九天。",
    instruction: "拖入影响因素，再转动历史年轮。",
    paragraph: "P10",
    sceneAssetId: "4D-01",
    evidenceAssetId: "4E-04",
    low: "1741—1750",
    high: "1921—1930"
  }
] as const;

export const productionScenes = [
  {
    id: "sow",
    cardPrompt: "根据当年花期预报，决定何时下种",
    targetLabel: "田间播种",
    targetContext: "需要确定本季作业时机",
    successExplanation: "物候资料可为选择播种日期提供参考。",
    icon: "播",
    regionAssetId: "5A-02",
    cardAssetId: "5B-01"
  },
  {
    id: "zone",
    cardPrompt: "比较多年物候资料，安排适宜种植区域",
    targetLabel: "作物分区",
    targetContext: "标有多个候选种植区域",
    successExplanation: "长期物候资料可辅助农作物区划。",
    icon: "区",
    regionAssetId: "5A-03",
    cardAssetId: "5B-02"
  },
  {
    id: "forest",
    cardPrompt: "结合开花结实期，确定造林和采种时机",
    targetLabel: "造林采种",
    targetContext: "等待安排季节性林业作业",
    successExplanation: "开花结实等物候记录可辅助安排造林和采种。",
    icon: "林",
    regionAssetId: "5A-04",
    cardAssetId: "5B-03"
  },
  {
    id: "introduce",
    cardPrompt: "比较两地物候条件后安排跨地区栽培",
    targetLabel: "植物引种",
    targetContext: "正在评估异地植物适应情况",
    successExplanation: "比较物候条件可为植物引种提供依据。",
    icon: "引",
    regionAssetId: "5A-05",
    cardAssetId: "5B-04"
  },
  {
    id: "pest",
    cardPrompt: "根据害虫生活节律预判防治窗口",
    targetLabel: "虫害防治",
    targetContext: "正在记录虫情与作物状态",
    successExplanation: "害虫物候记录可辅助选择防治时机。",
    icon: "防",
    regionAssetId: "5A-06",
    cardAssetId: "5B-05"
  },
  {
    id: "mountain",
    cardPrompt: "补充不同海拔的气候、土壤与作物观测",
    targetLabel: "山区农业",
    targetContext: "不同高度的田块等待连续调查",
    successExplanation: "山区农业需要结合物候、气候和土壤开展综合调查。",
    icon: "山",
    regionAssetId: "5A-07",
    cardAssetId: "5B-06"
  }
] as const;

export const localAiFallbacks: Record<ChapterId | "quiz", string> = {
  "chapter-1": "先别急着看答案。这个现象是否来自生物，并且会随时节反复出现？请用第1—3段核对。",
  "chapter-2": "课文把“活的仪器”限定为活生生的生物。再比较它们和仪表分别记录了什么。",
  "chapter-3": "一次只改变一个变量：南北、海陆、高度或年代。你能指出支持判断的段落吗？",
  "chapter-4": "把每项生产决策和第11段中的动词对应起来：播种、区划、造林、引种、防虫、调查。",
  quiz: "先标出结论中的关键词，再回到对应段落核验。我的判断也需要课文证据。"
};

// 根据章节编号返回稳定定义，供场景、HUD 与评分共用。
export function getChapter(id: ChapterId): ChapterDefinition {
  return chapters.find((chapter) => chapter.id === id) ?? chapters[0];
}
